import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { reChat, aed, dl } from "@/lib/docstate";

const sid = () => {
  let s = localStorage.getItem("ds_chat_session");
  if (!s) { s = "web-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("ds_chat_session", s); }
  return s;
};

const MiniCard = ({ l }) => (
  <Link to={dl(`/property/${l.reference || l.id}`)} className="flex gap-3 rounded-lg border border-[#C9A24B]/20 bg-[#0F0F11] p-2 transition-colors hover:border-[#C9A24B]/50" data-testid={`chat-suggestion-${l.reference || l.id}`}>
    <img src={l.cover_image} alt={l.title} className="h-16 w-20 shrink-0 rounded object-cover" />
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-medium text-white">{l.title}</p>
      <p className="truncate text-[11px] text-[#9D8D83]">{l.community}</p>
      <p className="mt-0.5 text-[10px] text-[#9D8D83]">
        {l.bedrooms ? `${l.bedrooms} bed` : ""}{l.bathrooms ? ` · ${l.bathrooms} bath` : ""}{l.area_sqft ? ` · ${Number(l.area_sqft).toLocaleString()} sqft` : ""}
      </p>
      <p className="text-xs font-semibold text-[#F3E5AB]">{aed(l.price, l.price_period)}</p>
    </div>
  </Link>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: `Hi, I'm Sara from Docstate. Looking to buy or rent in Dubai? Tell me the area and budget and I'll find the perfect fit.` },
  ]);
  const scroller = useRef(null);

  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [msgs, open, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const res = await reChat(sid(), text);
      setMsgs((m) => [...m, { role: "assistant", text: res.reply, suggestions: res.suggestions, agent: res.agent, qualified: res.qualified }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Apologies — I had a connection hiccup. Could you say that again?" }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-launcher"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold text-[#0B0B0C] shadow-2xl transition-transform hover:scale-105"
          style={{ background: "linear-gradient(135deg,#C9A24B,#F3E5AB,#B8912E)" }}>
          <MessageCircle className="h-5 w-5" /> Ask Sara
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[60] flex h-[560px] max-h-[82vh] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-[#C9A24B]/25 bg-[#111013] shadow-2xl" data-testid="chat-panel">
          <div className="flex items-center justify-between border-b border-[#C9A24B]/15 bg-[#0B0B0C] px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: "linear-gradient(135deg,#C9A24B,#B8912E)" }}><Sparkles className="h-4 w-4 text-[#0B0B0C]" /></span>
              <div><p className="text-sm font-semibold text-white">Sara · Docstate</p><p className="text-[11px] text-[#7fae7f]">● AI Property Advisor</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-[#9D8D83] hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[85%] space-y-2">
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-gradient-to-r from-[#C9A24B] to-[#B8912E] text-[#0B0B0C]" : "bg-[#1C1B20] text-[#DCD2C9]"}`} data-testid={`chat-msg-${m.role}`}>
                    {m.text}
                  </div>
                  {m.suggestions && m.suggestions.length > 0 && (
                    <div className="space-y-2">{m.suggestions.map((l) => <MiniCard key={l.id} l={l} />)}</div>
                  )}
                  {m.agent && m.agent.name && (
                    <div className="flex items-center gap-2 rounded-lg border border-[#C9A24B]/25 bg-[#0F0F11] p-2 text-xs text-[#C9BBB2]" data-testid="chat-agent-handoff">
                      {m.agent.photo && <img src={m.agent.photo} alt={m.agent.name} className="h-8 w-8 rounded-full object-cover" />}
                      <span>{m.agent.name} will reach out to you shortly.</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="rounded-2xl bg-[#1C1B20] px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-[#C9A24B]" /></div></div>}
          </div>

          <form onSubmit={send} className="border-t border-[#C9A24B]/15 bg-[#0B0B0C] p-3">
            <div className="flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message…" data-testid="chat-input"
                className="flex-1 rounded-full border border-[#C9A24B]/20 bg-[#0F0F11] px-4 py-2.5 text-sm text-white placeholder:text-[#6E635B] focus:border-[#C9A24B]/60 focus:outline-none" />
              <button type="submit" disabled={sending} data-testid="chat-send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#0B0B0C] disabled:opacity-50" style={{ background: "linear-gradient(135deg,#C9A24B,#B8912E)" }}>
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
